import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Modal API",
  "title": "Modal",
  "props": ["ModalProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DocsHeading = makeShortcode("DocsHeading");
const Link = makeShortcode("Link");
const ModalProps = makeShortcode("ModalProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<DocsHeading to="/design/modals/" type="api" mdxType="DocsHeading">
  Modal
</DocsHeading>
<h2>{`Basic Usage`}</h2>
<p>{`The `}<inlineCode parentName="p">{`Modal`}</inlineCode>{` component renders its `}<inlineCode parentName="p">{`children`}</inlineCode>{` node in front of a backdrop component. It also disables scrolling of the page content while open,
properly manages focus; moving to the modal content, and keeping it there until the modal is closed, and adds the appropriate ARIA roles automatically.`}</p>
<p>{`The modal will `}<em parentName="p">{`only`}</em>{` render if the `}<inlineCode parentName="p">{`isOpen`}</inlineCode>{` prop is set to true. For that reason, it is redundant to show the modal using the following anti-pattern: `}<inlineCode parentName="p">{`{flag && <Modal isOpen={flag} />`}</inlineCode>{`. Instead,
simply use `}<inlineCode parentName="p">{`<Modal isOpen={flag} />`}</inlineCode></p>
<p>{`Although not required, it is helpful to inform users (especially screen reader users) when a button or link will trigger a modal dialog.
This can be done by supplementing the button label or link text with "(opens modal dialog)" using the `}<Link to="/api/visually-hidden/" mdxType="Link">{`VisuallyHidden`}</Link>{` component.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Button, Hyperlink, Modal, VisuallyHidden } from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  function handleOnClose() {
    setShowModal(false);
    buttonRef.current.focus();
  }

  return (
    <>
      <Modal header="Modal Title" onClose={handleOnClose} isOpen={showModal}>
        <p>This is a modal, doing modal things.</p>
        <p>
          This is <Hyperlink to="#">linked text</Hyperlink> in the modal
        </p>
        <p>
          <Button>This is a button</Button>
        </p>
        <p>
          This is <Hyperlink to="#">some more linked text</Hyperlink> in the
          modal
        </p>
      </Modal>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal
        <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`Sizes`}</h2>
<p>{`Sizes for modals include `}<inlineCode parentName="p">{`small`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium`}</inlineCode>{`, and `}<inlineCode parentName="p">{`large`}</inlineCode>{`, with `}<inlineCode parentName="p">{`medium`}</inlineCode>{` being the default value.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import {
  Button,
  ButtonSize,
  Modal,
  ModalSize,
  VisuallyHidden,
  ButtonGroup,
} from 'react-magma-dom';
export function Example() {
  const [showSmallModal, setShowSmallModal] = React.useState(false);
  const [showLargeModal, setShowLargeModal] = React.useState(false);
  const smallButtonRef = React.useRef();
  const largeButtonRef = React.useRef();

  return (
    <>
      <Modal
        size={ModalSize.small}
        header="Modal Small"
        onClose={() => {
          setShowSmallModal(false);
          smallButtonRef.current.focus();
        }}
        isOpen={showSmallModal}
      >
        <p>This is a small modal, doing small modal things.</p>
      </Modal>

      <Modal
        size={ModalSize.large}
        header="Modal Large"
        onClose={() => {
          setShowLargeModal(false);
          largeButtonRef.current.focus();
        }}
        isOpen={showLargeModal}
      >
        <p>This is a large modal, doing large modal things.</p>
      </Modal>

      <ButtonGroup>
        <Button
          size={ButtonSize.small}
          onClick={() => setShowSmallModal(true)}
          ref={smallButtonRef}
        >
          Show Small Modal
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
        <Button
          size={ButtonSize.large}
          onClick={() => setShowLargeModal(true)}
          ref={largeButtonRef}
        >
          Show Large Modal
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
<h2>{`Modal Header`}</h2>
<p>{`The modal header prop is optional. It can accept a node or a string, and will be rendered inside an H1.
If there is a header passed in, the focus will be placed on the header when the modal opens. If not, the focus will be placed on
the first actionable element.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import {
  Button,
  Modal,
  ModalSize,
  VisuallyHidden,
  ButtonGroup,
  ButtonGroupAlignment,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [showModalHeader, setShowModalHeader] = React.useState(false);
  const buttonRef = React.useRef();
  const headerButtonRef = React.useRef();

  return (
    <>
      <Modal
        size={ModalSize.small}
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
      >
        <p>This modal has no header.</p>
        <ButtonGroup alignment={ButtonGroupAlignment.center}>
          <Button onClick={() => setShowModal(false)}>OK</Button>
        </ButtonGroup>
      </Modal>
      <Modal
        header="This modal has a header"
        size={ModalSize.small}
        onClose={() => {
          setShowModalHeader(false);
          headerButtonRef.current.focus();
        }}
        isOpen={showModalHeader}
      >
        <p>This modal has no header.</p>
        <ButtonGroup alignment={ButtonGroupAlignment.center}>
          <Button onClick={() => setShowModalHeader(false)}>OK</Button>
        </ButtonGroup>
      </Modal>
      <ButtonGroup>
        <Button onClick={() => setShowModal(true)} ref={buttonRef}>
          Show Modal with no header
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
        <Button onClick={() => setShowModalHeader(true)} ref={headerButtonRef}>
          Show Modal with header
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
<h2>{`Hide Close Button`}</h2>
<p>{`The close button can be hidden by using the `}<inlineCode parentName="p">{`isCloseButtonHidden`}</inlineCode>{` prop. If this prop is used, it is mandatory to provide another way to close the modal.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Button, Modal } from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  return (
    <>
      <Modal
        header="Modal Title"
        isCloseButtonHidden
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
      >
        <p>The standard modal close button is hidden.</p>
        <Button onClick={() => setShowModal(false)}>Close this Dialog</Button>
      </Modal>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`Custom Close Button`}</h2>
<p>{`If you would like to add a custom close button to the `}<inlineCode parentName="p">{`Modal`}</inlineCode>{` be sure not to use the same `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` function as it will mean that the function will be called twice (once internally and once by `}<inlineCode parentName="p">{`react-magma`}</inlineCode>{`).`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Button, Modal, VisuallyHidden } from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [magmaCloseCalledTimes, setMagmaCloseCalledTimes] = React.useState(0);
  const [internalCloseCalledTimes, setInternalCloseCalledTimes] =
    React.useState(0);
  const buttonRef = React.useRef();

  function closeModal() {
    setMagmaCloseCalledTimes(magmaCloseCalledTimes + 1);
    setShowModal(false);
    buttonRef.current.focus();
  }

  function customCloseModal() {
    setInternalCloseCalledTimes(internalCloseCalledTimes + 1);
    setShowModal(false);
    buttonRef.current.focus();
  }

  return (
    <>
      <Modal header="Modal Title" onClose={closeModal} isOpen={showModal}>
        Lorem ipsum dolar sit amet
        <Button onClick={customCloseModal}>Close Modal</Button>
      </Modal>
      <p>
        <strong>Magma Close Called Times:</strong> {magmaCloseCalledTimes}
      </p>
      <p>
        <strong>Internal Close Called Times:</strong> {internalCloseCalledTimes}
      </p>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`isInverse`}</h2>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import {
  Button,
  Hyperlink,
  Modal,
  VisuallyHidden,
  Card,
  CardBody,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  return (
    <>
      <Modal
        header="Modal Title"
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
        isInverse
      >
        <p>This is a modal, doing modal things.</p>
        <p>
          This is{' '}
          <Hyperlink to="#" isInverse>
            linked text
          </Hyperlink>{' '}
          in the modal
        </p>
        <p>
          <Button isInverse>This is a button</Button>
        </p>
        <p>
          This is{' '}
          <Hyperlink to="#" isInverse>
            some more linked text
          </Hyperlink>{' '}
          in the modal
        </p>
      </Modal>
      <Card isInverse>
        <CardBody>
          <Button onClick={() => setShowModal(true)} ref={buttonRef} isInverse>
            Show Modal
            <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
          </Button>
        </CardBody>
      </Card>
    </>
  );
}
`}</code></pre>
<h2>{`Modal Props`}</h2>
<p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element that wraps the Modal content.`}</strong></p>
<p>{`All of the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
      }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`div`}</inlineCode>{` element that wraps the modal content.`}</p>
<ModalProps mdxType="ModalProps" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      